// ||spoiler|| helpers — parsing, snippet masking, and the render wrapper.
// (Split from Spoiler.jsx so that file exports only its component — the
// react-refresh rule wants components and plain functions in separate files.)
//
// Parsing happens at RENDER time from the message content, so a message
// edited to add spoiler bars after the fact hides on the next paint — and
// each Spoiler is keyed by its own text, so editing what's inside a bar
// resets it to hidden instead of inheriting the old revealed state.

import React from 'react';
import Spoiler from './Spoiler.jsx';

/** How long a tapped spoiler stays open before re-hiding itself. */
export const REVEAL_MS = 5000;

/** Split text on ||spoiler|| spans. Unclosed or empty pairs stay literal. */
export function splitSpoilers(text) {
  const out = [];
  const re = /\|\|([\s\S]+?)\|\|/g;
  const s = String(text);
  let last = 0, m;
  while ((m = re.exec(s)) !== null) {
    if (m.index > last) out.push({ spoiler: false, text: s.slice(last, m.index) });
    out.push({ spoiler: true, text: m[1] });
    last = re.lastIndex;
  }
  if (last < s.length) out.push({ spoiler: false, text: s.slice(last) });
  return out;
}

/** True if the text contains at least one complete ||spoiler|| span. */
export function hasSpoilers(text) {
  return typeof text === 'string' && /\|\|[\s\S]+?\|\|/.test(text);
}

/** For one-line SNIPPETS (conversation previews, reply quotes, pins): mask
 * spoiler spans with a block glyph so a preview can't leak what the message
 * itself hides. Not interactive — snippets are not the reading surface. */
export function maskSpoilersForSnippet(text) {
  if (!hasSpoilers(text)) return text;
  return String(text).replace(/\|\|[\s\S]+?\|\|/g, '▮▮▮');
}

/**
 * The wiring point for every chat formatter: split the raw text on spoiler
 * bars, run the surface's own inline formatter on each segment, and wrap the
 * hidden ones. `renderInline(segmentText)` is the surface's existing
 * formatting pass — spoilers compose OUTSIDE it, so bold/emoji/links inside
 * a spoiler still format (they're just invisible until tapped).
 */
export function renderWithSpoilers(text, renderInline) {
  if (!hasSpoilers(text)) return renderInline(text);
  return splitSpoilers(text).map((seg, i) => seg.spoiler
    ? <Spoiler key={`sp-${i}-${seg.text}`}>{renderInline(seg.text)}</Spoiler>
    : <React.Fragment key={`tx-${i}`}>{renderInline(seg.text)}</React.Fragment>);
}
